import React from "react";
import PropTypes from "prop-types";

const MAX_PAGE_SLOTS = 7;

export function pageSlots(page, pageCount) {
  if (pageCount <= MAX_PAGE_SLOTS) {
    return Array.from({ length: pageCount }, (_, index) => index + 1);
  }

  if (page <= 4) {
    return [1, 2, 3, 4, 5, "end-ellipsis", pageCount];
  }
  if (page >= pageCount - 3) {
    return [1, "start-ellipsis", ...Array.from({ length: 5 }, (_, index) => pageCount - 4 + index)];
  }
  return [1, "start-ellipsis", page - 1, page, page + 1, "end-ellipsis", pageCount];
}

/**
 * PaginationGroup — Figma's "Pagination / Group": Previous and Next buttons either side of the page
 * numbers, which collapse to first, last and the current page's neighbours once there are more than
 * seven pages. Renders nothing for a single page. At either end Previous or Next is only marked
 * disabled (aria-disabled), so a keyboard user who just pressed it keeps their focus there.
 *
 * @param {Object} props
 * @param {number} props.page - The current page, 1-based
 * @param {number} props.pageCount - Total pages
 * @param {Function} props.onPageChange - Called with the page number to show
 * @param {string} [props.className] - Extra classes for the wrapper
 * @param {string} [props.ariaLabel] - Accessible name for the nav landmark (default "Pagination")
 * @param {boolean} [props.disabled] - Disables every control, e.g. while a row is being edited
 */
function PaginationGroup({
  page,
  pageCount,
  onPageChange,
  className = "",
  ariaLabel = "Pagination",
  disabled = false,
}) {
  if (pageCount <= 1) {
    return null;
  }

  const goTo = (target) => {
    if (target !== page && target >= 1 && target <= pageCount) {
      onPageChange(target);
    }
  };
  const atFirst = page <= 1;
  const atLast = page >= pageCount;
  const stepClasses = (atEnd) => `btn btn-secondary pagination-group-step${atEnd ? " disabled" : ""}`;

  return (
    <nav className={`pagination-group ${className}`.trim()} aria-label={ariaLabel}>
      <div className="pagination-group-row">
        <button
          type="button"
          className={stepClasses(atFirst)}
          disabled={disabled}
          aria-disabled={atFirst || undefined}
          onClick={() => goTo(page - 1)}
        >
          <i className="fa-solid fa-arrow-left" aria-hidden="true" />
          Previous
        </button>

        <ol className="pagination-group-pages list-unstyled mb-0">
          {pageSlots(page, pageCount).map((slot) =>
            typeof slot === "number" ? (
              <li key={slot}>
                <button
                  type="button"
                  className={`pagination-group-page${slot === page ? " active" : ""}`}
                  aria-label={`Page ${String(slot).padStart(2, "0")}`}
                  aria-current={slot === page ? "page" : undefined}
                  disabled={disabled}
                  onClick={() => goTo(slot)}
                >
                  {String(slot).padStart(2, "0")}
                </button>
              </li>
            ) : (
              <li key={slot} className="pagination-group-ellipsis" aria-hidden="true">
                ...
              </li>
            ),
          )}
        </ol>

        <button
          type="button"
          className={stepClasses(atLast)}
          disabled={disabled}
          aria-disabled={atLast || undefined}
          onClick={() => goTo(page + 1)}
        >
          Next
          <i className="fa-solid fa-arrow-right" aria-hidden="true" />
        </button>
      </div>
    </nav>
  );
}

PaginationGroup.propTypes = {
  page: PropTypes.number.isRequired,
  pageCount: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
  className: PropTypes.string,
  ariaLabel: PropTypes.string,
  disabled: PropTypes.bool,
};

export default PaginationGroup;
